import * as React from 'react'
import { Link, useSearchParams } from 'react-router'
import { Check, Loader2 } from 'lucide-react'
import { parseMoney } from '@lib/helpers/money'
import { formatTaxRatePercent, parseTaxRatePercent } from '@lib/helpers/tax'
import type {
  OrganizationDto,
  OrganizationInsurance,
} from '@lib/dto/organization.dto'
import { updateOrganizationInputSchema } from '@lib/input/organization.input'
import { useOrganization, useUpdateOrganization } from '@/hooks/useOrganization'
import { OrganizationIdentitySettings } from '@/components/OrganizationIdentitySettings'
import { ProposalRulesSettings } from '@/components/ProposalRulesSettings'
import { errorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TreeSpeciesSettings } from '@/components/TreeSpeciesSettings'
import { TreeProblemSettings } from '@/components/TreeProblemSettings'
import { TreeJobSettings } from '@/components/TreeJobSettings'
import { SeasonSettings } from '@/components/SeasonSettings'
import { ProposalWordingSettings } from '@/components/ProposalWordingSettings'
import { cn } from '@/lib/utils'
import { HousecallProSettings } from '@/components/HousecallProSettings'
import { HcpWritesSetting } from '@/components/HcpWritesSetting'
import { IrrigationPricingSettings } from '@/components/IrrigationPricingSettings'
import { IrrigationProblemSettings } from '@/components/IrrigationProblemSettings'

/**
 * Organization-wide settings — everything here is shared by everyone.
 *
 * Personal controls live on the account page instead. A password briefly sat
 * on this page and read as though it applied to the whole organization, which
 * is the one thing it does not.
 *
 * Inside the app shell rather than full-bleed like the map screens: this is a
 * desk task done once in a while, not something anyone does standing in a park.
 *
 * Every signed-in user can change these, matching the rest of the app — one
 * organization, one user type. Worth noticing that a rate feeding every quote
 * is the sort of setting that should stop being editable by everyone the moment
 * a second kind of user exists.
 */
/**
 * The parts of Settings, in the order somebody setting up the business needs
 * them: who it is, what it charges, what it carries, its trees and irrigation,
 * then the words.
 */
const SETTINGS_SECTIONS = [
  { key: 'business', label: 'Business' },
  { key: 'pricing', label: 'Pricing and rules' },
  { key: 'insurance', label: 'Insurance and credentials' },
  { key: 'trees', label: 'Trees' },
  { key: 'irrigation', label: 'Irrigation' },
  { key: 'wording', label: 'Proposal wording' },
] as const

type SettingsSectionKey = (typeof SETTINGS_SECTIONS)[number]['key']

/** The section named in the URL, or the first one. */
function toSectionKey(value: string | null): SettingsSectionKey {
  return (
    SETTINGS_SECTIONS.find((section) => section.key === value)?.key ??
    'business'
  )
}

export function SettingsPage() {
  const organization = useOrganization()
  const [params] = useSearchParams()
  // In the URL, so a link can open the right section ("set a default in
  // Settings") and a reload stays where somebody was.
  const current = toSectionKey(params.get('section'))

  if (organization.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-text" />
      </div>
    )
  }

  if (organization.isError) {
    return (
      <div className="max-w-2xl rounded-lg border border-default bg-surface px-3 py-12 text-center">
        <p className="text-sm font-medium">Settings could not be loaded</p>
        <p className="mt-1 text-sm text-muted-text">
          Check your connection and try again.
        </p>
      </div>
    )
  }

  const data = organization.data

  /*
   * Each section saves on its own. The API takes a partial patch, so a
   * section sends only its own fields — saving the labor rate cannot
   * overwrite an insurance limit somebody changed in another tab.
   */
  function render(key: SettingsSectionKey): React.ReactNode {
    switch (key) {
      case 'business':
        return (
          <>
            <OrganizationIdentitySettings organization={data} />
            {/* Here, not under Trees or Irrigation: it covers clients and both kinds of job. */}
            <HcpWritesSetting />
          </>
        )
      case 'pricing':
        return (
          <>
            <PricingSection organization={data} />
            {/* Beside pricing: both decide what a proposal comes to. */}
            <ProposalRulesSettings organization={data} />
          </>
        )
      case 'insurance':
        return <InsuranceSection organization={data} />
      case 'trees':
        /*
          Each loads its own data rather than taking it from this page: the
          catalogs are their own endpoints, and threading them through here
          would make settings the only reason two unrelated queries share a
          parent.
        */
        return (
          <>
            <TreeSpeciesSettings />
            <TreeProblemSettings />
            <SeasonSettings organization={data} />
            <TreeJobSettings />
          </>
        )
      case 'irrigation':
        return (
          <>
            <HousecallProSettings />
            <IrrigationPricingSettings />
            <IrrigationProblemSettings />
          </>
        )
      case 'wording':
        return <ProposalWordingSettings />
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Settings</h1>
      <div className="grid gap-4 lg:grid-cols-[12rem_minmax(0,42rem)] lg:gap-8">
        <nav
          aria-label="Settings sections"
          className="overflow-x-auto"
        >
          <ul className="flex gap-1 lg:sticky lg:top-4 lg:flex-col">
            {SETTINGS_SECTIONS.map((section) => (
              <li key={section.key}>
                <Link
                  to={`?section=${section.key}`}
                  replace
                  aria-current={section.key === current ? 'page' : undefined}
                  className={cn(
                    'block rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap transition-colors',
                    section.key === current
                      ? 'bg-subtle font-medium text-primary-text'
                      : 'text-secondary-text hover:bg-subtle hover:text-primary-text',
                  )}
                >
                  {section.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        {/*
          Every section stays mounted and only the chosen one shows. Each
          holds its own unsaved edits, and moving to another section to check
          something must not throw them away.
        */}
        <div className="min-w-0">
          {SETTINGS_SECTIONS.map((section) => (
            <div
              key={section.key}
              hidden={section.key !== current}
              className="space-y-6"
            >
              {render(section.key)}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** The shared frame: heading, blurb, body, then a save row. */
function SettingsSection({
  title,
  description,
  error,
  isDirty,
  canSave,
  isSaving,
  justSaved,
  onSave,
  children,
}: {
  title: string
  description: string
  error: string | null
  isDirty: boolean
  canSave: boolean
  isSaving: boolean
  justSaved: boolean
  onSave: () => void
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3 rounded-lg border border-default bg-surface p-4">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-0.5 text-sm text-muted-text">{description}</p>
      </div>

      {children}

      {error !== null ? <p className="text-sm text-danger">{error}</p> : null}

      <div className="flex items-center gap-3 border-t border-default pt-3">
        <Button disabled={!canSave || isSaving} onClick={onSave}>
          {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Save changes
        </Button>
        {justSaved && !isDirty ? (
          <span className="flex items-center gap-1 text-sm text-muted-text">
            <Check className="h-3.5 w-3.5" />
            Saved
          </span>
        ) : null}
      </div>
    </section>
  )
}

/** A dollar field with the symbol inside it and an optional trailing unit. */
function MoneyField({
  id,
  label,
  value,
  placeholder,
  unit,
  help,
  invalidHelp = 'Enter an amount in dollars, e.g. 2000000',
  isValid,
  prefix = '$',
  onChange,
}: {
  id: string
  label: string
  value: string
  placeholder: string
  unit?: string
  help?: string
  invalidHelp?: string
  isValid: boolean
  /** A symbol inside the field. Empty for a rate that is not dollars. */
  prefix?: string
  onChange: (value: string) => void
}) {
  const padLeft = prefix === '' ? '' : 'pl-6'
  const padRight = unit === undefined ? '' : 'pr-14'

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        {/* A prefixed symbol rather than a "$" in the label: it keeps the unit
            attached to the number while it is being typed. */}
        {prefix === '' ? null : (
          <span className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-sm text-muted-text">
            {prefix}
          </span>
        )}
        <Input
          id={id}
          value={value}
          inputMode="decimal"
          placeholder={placeholder}
          aria-invalid={!isValid}
          aria-describedby={help === undefined ? undefined : `${id}-help`}
          className={[padRight, padLeft]
            .filter((part) => part !== '')
            .join(' ')}
          onChange={(event) => {
            onChange(event.target.value)
          }}
        />
        {unit === undefined ? null : (
          <span className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-sm text-muted-text">
            {unit}
          </span>
        )}
      </div>
      {help === undefined ? null : (
        <p id={`${id}-help`} className="text-xs text-muted-text">
          {isValid ? help : invalidHelp}
        </p>
      )}
    </div>
  )
}

function TextField({
  id,
  label,
  value,
  placeholder,
  onChange,
}: {
  id: string
  label: string
  value: string
  placeholder: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
    </div>
  )
}

/**
 * A native checkbox rather than a Radix one.
 *
 * Two booleans do not justify a new dependency and a new primitive, and the
 * native control is keyboard- and screen-reader-correct without any of it.
 */
function CheckField({
  id,
  label,
  help,
  checked,
  onChange,
}: {
  id: string
  label: string
  help: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <div className="flex gap-2.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
        onChange={(event) => {
          onChange(event.target.checked)
        }}
      />
      <div className="space-y-0.5">
        <Label htmlFor={id}>{label}</Label>
        <p className="text-xs text-muted-text">{help}</p>
      </div>
    </div>
  )
}

/**
 * A stored tax rate as it should appear in the field.
 *
 * The wire format keeps three decimal places, which is right for 8.875% and
 * wrong for a text box: "8.250" is a character somebody has to click past.
 * The dirty check compares thousandths either way, so dropping the zeros
 * here cannot make a saved rate look unsaved.
 */
function toTaxText(value: string | null): string {
  if (value === null) return ''
  return formatTaxRatePercent(value) ?? ''
}

/**
 * Typed days as the number the API will accept, or null if it would refuse.
 * Checked against the shared input schema, so the bounds live in one place.
 */
function parseValidityDays(text: string): number | null {
  if (!/^\d+$/.test(text)) return null
  const parsed =
    updateOrganizationInputSchema.shape.proposalValidityDays.safeParse(
      Number(text),
    )
  return parsed.success ? (parsed.data ?? null) : null
}

function PricingSection({ organization }: { organization: OrganizationDto }) {
  const updateOrganization = useUpdateOrganization()

  /**
   * The fields hold raw text, not the saved values.
   *
   * Deriving them from the record would fight the person typing: "85." is not
   * a valid rate, so it would never commit, so it would be wiped on the next
   * render and the decimal point could never be entered.
   */
  const [rateText, setRateText] = React.useState('')
  const [taxText, setTaxText] = React.useState('')
  const [daysText, setDaysText] = React.useState('')
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [justSaved, setJustSaved] = React.useState(false)

  const savedRate = organization.treeLaborRatePerHour
  const savedTax = organization.taxRatePercent
  const savedDays = organization.proposalValidityDays

  // Adopt the server's values once they arrive, and after a save. Keyed on
  // the values themselves, so a change made elsewhere is picked up rather
  // than sitting stale behind a form nobody is editing.
  React.useEffect(() => {
    setRateText(savedRate ?? '')
    setTaxText(toTaxText(savedTax))
    setDaysText(savedDays === null ? '' : String(savedDays))
  }, [savedRate, savedTax, savedDays])

  const rateTrimmed = rateText.trim()
  const taxTrimmed = taxText.trim()
  const rateBlank = rateTrimmed === ''
  const taxBlank = taxTrimmed === ''
  const rateValid = rateBlank || parseMoney(rateTrimmed) !== null
  const taxValid = taxBlank || parseTaxRatePercent(taxTrimmed) !== null
  /*
   * Compared as numbers, not as text. "85" and "85.00" are the same labor
   * rate, and "8.25" and "8.250" are the same tax rate. A Save button that
   * lit up because the server echoed back a normalized string would be
   * claiming an unsaved change that does not exist.
   */
  const rateDirty =
    rateValid &&
    parseMoney(rateBlank ? null : rateTrimmed) !== parseMoney(savedRate)
  const taxDirty =
    taxValid &&
    parseTaxRatePercent(taxBlank ? null : taxTrimmed) !==
      parseTaxRatePercent(savedTax)
  const daysTrimmed = daysText.trim()
  const daysBlank = daysTrimmed === ''
  const days = daysBlank ? null : parseValidityDays(daysTrimmed)
  const daysValid = daysBlank || days !== null
  const daysDirty = daysValid && days !== savedDays
  const isDirty = rateDirty || taxDirty || daysDirty
  const isValid = rateValid && taxValid && daysValid

  async function handleSave(): Promise<void> {
    setSaveError(null)
    setJustSaved(false)
    try {
      await updateOrganization.mutateAsync({
        ...(rateDirty
          ? { treeLaborRatePerHour: rateBlank ? null : rateTrimmed }
          : {}),
        ...(taxDirty ? { taxRatePercent: taxBlank ? null : taxTrimmed } : {}),
        ...(daysDirty ? { proposalValidityDays: days } : {}),
      })
      setJustSaved(true)
    } catch (caught) {
      setSaveError(errorMessage(caught, 'Could not save settings. Please try again.'))
    }
  }

  return (
    <SettingsSection
      title="Pricing"
      description="What tree work costs per crew-hour, the tax added to a proposal total, and how long a quoted price holds. Estimated labor recorded against each tree is priced at this rate."
      error={saveError}
      isDirty={isDirty}
      canSave={isDirty && isValid}
      isSaving={updateOrganization.isPending}
      justSaved={justSaved}
      onSave={() => {
        void handleSave()
      }}
    >
      <div className="grid gap-4 sm:max-w-xl sm:grid-cols-2">
        <MoneyField
          id="tree-labor-rate"
          label="Tree labor rate"
          value={rateText}
          placeholder="85.00"
          unit="/ hour"
          help="Leave blank if no rate is set yet — work simply will not be priced."
          isValid={rateValid}
          onChange={(value) => {
            setRateText(value)
            setJustSaved(false)
          }}
        />
        <MoneyField
          id="tax-rate"
          label="Tax rate"
          value={taxText}
          placeholder="8.25"
          unit="%"
          prefix=""
          help="Added to every proposal total. Leave blank to charge no tax."
          invalidHelp="Enter a percent, up to 100, e.g. 8.25"
          isValid={taxValid}
          onChange={(value) => {
            setTaxText(value)
            setJustSaved(false)
          }}
        />
        <MoneyField
          id="proposal-validity-days"
          label="Price holds for"
          value={daysText}
          placeholder="30"
          unit="days"
          prefix=""
          help="Counted from when a proposal is sent, and printed on its cover. Leave blank for no expiry date."
          invalidHelp="Enter a whole number of days, from 1 to 365"
          isValid={daysValid}
          onChange={(value) => {
            setDaysText(value)
            setJustSaved(false)
          }}
        />
      </div>
    </SettingsSection>
  )
}

/**
 * The insurance fields, as text, so a half-typed number survives a render.
 * The checkboxes stay booleans. Keyed by what is stored, so a credential added
 * there has to be added to this form.
 */
type InsuranceDraft = {
  [K in keyof OrganizationInsurance]: OrganizationInsurance[K] extends boolean
    ? boolean
    : string
}

/** The money fields, which validate and compare differently from the rest. */
const MONEY_FIELDS = [
  'generalLiabilityPerOccurrence',
  'generalLiabilityAggregate',
  'autoLiabilityPerAccident',
] as const

/** The plain text fields, compared as trimmed strings. */
const TEXT_FIELDS = [
  'insuranceCarrier',
  'isaCertificationNumber',
  'contractorLicenseNumber',
  'irrigatorLicenseNumber',
  'backflowTesterLicenseNumber',
] as const

/**
 * A stored limit as it should appear in the field.
 *
 * The wire format is always two decimal places, which is right for exactness
 * and wrong for a text box: coverage limits are round numbers, and "2000000.00"
 * is three characters somebody has to click past to edit. Cents survive when a
 * value actually has them, and the dirty check compares cents either way, so
 * dropping them here cannot make a saved value look unsaved.
 */
function toLimitText(value: string | null): string {
  return value === null ? '' : value.replace(/\.00$/, '')
}

function toDraft(organization: OrganizationDto): InsuranceDraft {
  return {
    insuranceCarrier: organization.insuranceCarrier ?? '',
    generalLiabilityPerOccurrence: toLimitText(
      organization.generalLiabilityPerOccurrence,
    ),
    generalLiabilityAggregate: toLimitText(
      organization.generalLiabilityAggregate,
    ),
    autoLiabilityPerAccident: toLimitText(
      organization.autoLiabilityPerAccident,
    ),
    carriesWorkersCompensation: organization.carriesWorkersCompensation,
    providesAdditionalInsured: organization.providesAdditionalInsured,
    isaCertificationNumber: organization.isaCertificationNumber ?? '',
    contractorLicenseNumber: organization.contractorLicenseNumber ?? '',
    licenseState: organization.licenseState ?? '',
    irrigatorLicenseNumber: organization.irrigatorLicenseNumber ?? '',
    backflowTesterLicenseNumber: organization.backflowTesterLicenseNumber ?? '',
  }
}

/** Blank means "not recorded", which the proposal omits rather than prints. */
function orNull(value: string): string | null {
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Insurance and credentials.
 *
 * These live here rather than in the proposal template because they are facts
 * about the business, not drafting decisions: a policy renews and a limit
 * changes, and that should not need a deploy. How they are WORDED on a proposal
 * still lives in `api/src/proposals/document/proposal-template.ts`.
 *
 * Anything left blank is left off the proposal entirely. A heading over five
 * labeled blanks reads as a document that failed to load rather than as a
 * claim nobody made.
 */
function InsuranceSection({ organization }: { organization: OrganizationDto }) {
  const updateOrganization = useUpdateOrganization()

  const saved = React.useMemo(() => toDraft(organization), [organization])
  const [draft, setDraft] = React.useState<InsuranceDraft>(saved)
  const [saveError, setSaveError] = React.useState<string | null>(null)
  const [justSaved, setJustSaved] = React.useState(false)

  // Adopt the server's values whenever they change, for the same reason the
  // rate field does: a change made elsewhere should not sit stale here.
  React.useEffect(() => {
    setDraft(saved)
  }, [saved])

  function set<K extends keyof InsuranceDraft>(
    field: K,
    value: InsuranceDraft[K],
  ): void {
    setDraft((current) => ({ ...current, [field]: value }))
    setJustSaved(false)
  }

  const invalidMoney = MONEY_FIELDS.filter((field) => {
    const value = draft[field].trim()
    return value !== '' && parseMoney(value) === null
  })
  // The same check the update runs, including the blank that clears the state.
  const stateIsValid = updateOrganizationInputSchema.shape.licenseState.safeParse(
    draft.licenseState,
  ).success
  const isValid = invalidMoney.length === 0 && stateIsValid

  /*
   * Money is compared as cents, so "2000000" and "2000000.00" are one value and
   * the server echoing back a normalized string does not light up Save. State
   * is compared case-insensitively, since the API upper-cases it on the way in.
   */
  const isDirty =
    isValid &&
    (MONEY_FIELDS.some(
      (field) => parseMoney(orNull(draft[field])) !== parseMoney(saved[field]),
    ) ||
      TEXT_FIELDS.some(
        (field) => orNull(draft[field]) !== orNull(saved[field]),
      ) ||
      orNull(draft.licenseState)?.toUpperCase() !==
        orNull(saved.licenseState)?.toUpperCase() ||
      draft.carriesWorkersCompensation !== saved.carriesWorkersCompensation ||
      draft.providesAdditionalInsured !== saved.providesAdditionalInsured)

  async function handleSave(): Promise<void> {
    setSaveError(null)
    setJustSaved(false)
    // The draft is plain text. The update's license state is a checked value,
    // so the schema has to produce it — annotating this object would claim
    // the check had already happened.
    const parsed = updateOrganizationInputSchema.safeParse({
      insuranceCarrier: orNull(draft.insuranceCarrier),
      generalLiabilityPerOccurrence: orNull(
        draft.generalLiabilityPerOccurrence,
      ),
      generalLiabilityAggregate: orNull(draft.generalLiabilityAggregate),
      autoLiabilityPerAccident: orNull(draft.autoLiabilityPerAccident),
      carriesWorkersCompensation: draft.carriesWorkersCompensation,
      providesAdditionalInsured: draft.providesAdditionalInsured,
      isaCertificationNumber: orNull(draft.isaCertificationNumber),
      contractorLicenseNumber: orNull(draft.contractorLicenseNumber),
      licenseState: orNull(draft.licenseState),
      irrigatorLicenseNumber: orNull(draft.irrigatorLicenseNumber),
      backflowTesterLicenseNumber: orNull(draft.backflowTesterLicenseNumber),
    })
    if (!parsed.success) {
      setSaveError(
        parsed.error.issues[0]?.message ??
          'Could not save settings. Please try again.',
      )
      return
    }
    try {
      await updateOrganization.mutateAsync(parsed.data)
      setJustSaved(true)
    } catch (caught) {
      setSaveError(
        errorMessage(caught, 'Could not save settings. Please try again.'),
      )
    }
  }

  return (
    <SettingsSection
      title="Insurance and credentials"
      description="Shown on every proposal. A board usually has to verify these before awarding work, so having them on the page saves a round trip. Anything left blank is left off."
      error={saveError}
      isDirty={isDirty}
      canSave={isDirty && isValid}
      isSaving={updateOrganization.isPending}
      justSaved={justSaved}
      onSave={() => {
        void handleSave()
      }}
    >
      <div className="space-y-4">
        <div className="max-w-sm">
          <TextField
            id="insurance-carrier"
            label="Insurance carrier"
            value={draft.insuranceCarrier}
            placeholder="Acme Mutual"
            onChange={(value) => {
              set('insuranceCarrier', value)
            }}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <MoneyField
            id="gl-per-occurrence"
            label="General liability, per occurrence"
            value={draft.generalLiabilityPerOccurrence}
            placeholder="2000000"
            isValid={!invalidMoney.includes('generalLiabilityPerOccurrence')}
            onChange={(value) => {
              set('generalLiabilityPerOccurrence', value)
            }}
          />
          <MoneyField
            id="gl-aggregate"
            label="General liability, aggregate"
            value={draft.generalLiabilityAggregate}
            placeholder="4000000"
            isValid={!invalidMoney.includes('generalLiabilityAggregate')}
            onChange={(value) => {
              set('generalLiabilityAggregate', value)
            }}
          />
          <MoneyField
            id="auto-liability"
            label="Auto liability, per accident"
            value={draft.autoLiabilityPerAccident}
            placeholder="1000000"
            isValid={!invalidMoney.includes('autoLiabilityPerAccident')}
            onChange={(value) => {
              set('autoLiabilityPerAccident', value)
            }}
          />
        </div>

        {invalidMoney.length > 0 ? (
          <p className="text-xs text-danger">
            Enter amounts in dollars, e.g. 2000000
          </p>
        ) : null}

        <div className="space-y-2.5 border-t border-default pt-3">
          <CheckField
            id="workers-comp"
            label="Workers’ compensation"
            help="Carried on all crew members."
            checked={draft.carriesWorkersCompensation}
            onChange={(checked) => {
              set('carriesWorkersCompensation', checked)
            }}
          />
          <CheckField
            id="additional-insured"
            label="Will name the client as additional insured"
            help="A certificate of insurance is provided on request."
            checked={draft.providesAdditionalInsured}
            onChange={(checked) => {
              set('providesAdditionalInsured', checked)
            }}
          />
        </div>

        <div className="grid gap-4 border-t border-default pt-3 sm:grid-cols-[1fr_1fr_6rem]">
          <TextField
            id="isa-number"
            label="ISA certification no."
            value={draft.isaCertificationNumber}
            placeholder="TX-1234A"
            onChange={(value) => {
              set('isaCertificationNumber', value)
            }}
          />
          <TextField
            id="license-number"
            label="License no."
            value={draft.contractorLicenseNumber}
            placeholder="0012345"
            onChange={(value) => {
              set('contractorLicenseNumber', value)
            }}
          />
          <div className="space-y-1.5">
            <Label htmlFor="license-state">State</Label>
            <Input
              id="license-state"
              value={draft.licenseState}
              placeholder="TX"
              maxLength={2}
              aria-invalid={!stateIsValid}
              className="uppercase"
              onChange={(event) => {
                set('licenseState', event.target.value)
              }}
            />
          </div>
        </div>

        {stateIsValid ? null : (
          <p className="text-xs text-danger">
            Enter a two-letter state, e.g. TX
          </p>
        )}

        <div className="space-y-2 border-t border-default pt-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="irrigator-license"
              label="Licensed irrigator no."
              value={draft.irrigatorLicenseNumber}
              placeholder="LI0012345"
              onChange={(value) => {
                set('irrigatorLicenseNumber', value)
              }}
            />
            <TextField
              id="backflow-license"
              label="Backflow tester no."
              value={draft.backflowTesterLicenseNumber}
              placeholder="BP0012345"
              onChange={(value) => {
                set('backflowTesterLicenseNumber', value)
              }}
            />
          </div>
          <p className="text-xs text-muted-text">
            Shown on proposals that bid irrigation. The ISA certification is
            shown on proposals that bid tree work.
          </p>
        </div>
      </div>
    </SettingsSection>
  )
}
